import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';
import { AUTH_ENVIRONMENT } from '../tokens/auth-environment.token';

/** Nombre de la cookie de sesión del tenant */
const COOKIE_NAME = '.hta_tenant_session';

/**
 * Función auxiliar para verificar si existe una cookie
 */
function hasCookie(name: string): boolean {
    return document.cookie.split(';').some(cookie => 
        cookie.trim().startsWith(name + '=')
    );
}

/** Rutas que deben omitir la verificación de cookie del tenant */
const TENANT_SELECTION_ROUTES = ['/tenant-selection', '/select-tenant', '/tenants', '/create-tenant', '/invitation/accept'];

/** 
 * Promesa compartida para evitar llamadas concurrentes al endpoint de sesión.
 */
let sessionCreationPromise: Promise<void> | null = null;

/**
 * Clona la petición agregando withCredentials: true para enviar cookies
 */
function cloneWithCredentials(req: HttpRequest<unknown>, ...baseUrls: string[]): HttpRequest<unknown> {
    // Agregar withCredentials para cualquier petición a los backends conocidos
    if (baseUrls.some(u => req.url.includes(u)) || req.url.startsWith('/api')) {
        return req.clone({ withCredentials: true });
    }
    return req;
}

/**
 * Interceptor que:
 * 1. Agrega withCredentials: true para enviar la cookie de sesión en cada request
 * 2. Verifica si existe la cookie de sesión del tenant
 * 3. Si no existe pero hay currentTenant en Cognito, crea la sesión
 * 4. Redirige a selección de tenant si no hay ninguno seleccionado
 */
export const cookieInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const tenantService = inject(TenantService);
    const router = inject(Router);
    const env = inject(AUTH_ENVIRONMENT);
    
    const noAuthRequired = ['assets/', 'i18n/', 'icons/'];
    const tenantApiRoutes = ['/api/auth/tenants', '/api/auth/select-tenant', '/api/auth/current-tenant', '/auth/tenants', '/auth/select-tenant', '/auth/current-tenant', '/auth/session', '/api/invitations'];

    // Omitir verificación para recursos estáticos
    if (noAuthRequired.some((str) => req.url.includes(str))) {
        return next(req);
    }

    // Clonar petición con withCredentials para enviar cookie a todos los backends conocidos
    const allBackendUrls = [env.htaBaseUrl, ...Object.values(env.modules ?? {})];
    const reqWithCredentials = cloneWithCredentials(req, ...allBackendUrls);

    // Solo verificar si el usuario está autenticado
    if (!authService.isAuthenticated()) {
        return next(reqWithCredentials);
    }

    // Omitir verificación para llamadas API relacionadas con tenants
    if (tenantApiRoutes.some((route) => req.url.includes(route))) {
        return next(reqWithCredentials);
    }

    // Omitir verificación para POST/PUT a /tenants (crear o actualizar tenant)
    if (req.url.includes('/tenants') && (req.method === 'POST' || req.method === 'PUT')) {
        return next(reqWithCredentials);
    }

    // Verificar si ya estamos en una ruta de selección de tenant
    const currentUrl = router.url;
    if (TENANT_SELECTION_ROUTES.some((route) => currentUrl.includes(route))) {
        return next(reqWithCredentials);
    }

    // Si la sesión ya está establecida en memoria (signal), continuar directamente.
    // Esto evita recrear la sesión en cada refresh cuando el signal se re-hidrata
    // antes de que lleguen otras peticiones (resolver secuencial).
    if (tenantService.sessionEstablished()) {
        return next(reqWithCredentials).pipe(
            catchError((err: HttpErrorResponse) => {
                const isTenantRequired = err.status === 403 &&
                    (err.error?.code === 'TENANT_REQUIRED' || err.error?.error?.includes('Tenant'));
                if (isTenantRequired) {
                    tenantService.clearState();
                    router.navigate(['/tenant-selection']);
                    return EMPTY;
                }
                return throwError(() => err);
            })
        );
    }

    // Si la cookie de sesión ya existe, continuar con la petición.
    // También interceptamos la respuesta: si el servidor devuelve 403 con
    // código TENANT_REQUIRED significa que la cookie fue rechazada/expirada.
    // En ese caso recreamos la sesión y reintentamos UNA vez.
    if (hasCookie(COOKIE_NAME)) {
        return next(reqWithCredentials).pipe(
            catchError((err: HttpErrorResponse) => {
                const isTenantRequired = err.status === 403 &&
                    (err.error?.code === 'TENANT_REQUIRED' || err.error?.error?.includes('Tenant'));
                if (isTenantRequired) {
                    // La cookie fue rechazada por el servidor; recrear sesión
                    return from(authService.getCurrentPoolUser()).pipe(
                        switchMap((user) => {
                            const cognitoId = user?.sub;
                            if (!cognitoId) {
                                router.navigate(['/tenant-selection']);
                                return EMPTY;
                            }
                            return from(authService.getTenantAttributes()).pipe(
                                switchMap(({ currentTenant }) => {
                                    if (currentTenant === null) {
                                        router.navigate(['/tenant-selection']);
                                        return EMPTY;
                                    }
                                    return from(tenantService.createSession(cognitoId, currentTenant)).pipe(
                                        switchMap(() => next(reqWithCredentials)),
                                        catchError(() => {
                                            router.navigate(['/tenant-selection']);
                                            return EMPTY;
                                        })
                                    );
                                })
                            );
                        })
                    );
                }
                return throwError(() => err);
            })
        );
    }

    // Si no hay cookie, verificar los atributos de Cognito
    return from(authService.getTenantAttributes()).pipe(
        switchMap(({ currentTenant, tenantIds }) => {
            // Si tiene currentTenant en Cognito pero no tiene la cookie, crear la sesión
            if (currentTenant !== null) {
                // Verificar nuevamente la cookie (pudo haberse creado mientras esperábamos)
                if (hasCookie(COOKIE_NAME)) {
                    return next(reqWithCredentials);
                }

                // Si ya hay una creación de sesión en progreso, esperar a que termine
                if (sessionCreationPromise) {
                    return from(sessionCreationPromise).pipe(
                        switchMap(() => next(reqWithCredentials)),
                        catchError((err) => {
                            // La sesión no pudo crearse: no reenviar la petición sin cookie
                            console.error('Error esperando sesión de tenant (concurrent):', err);
                            const status = err?.status;
                            if (status === 401 || status === 403) {
                                router.navigate(['/tenant-selection']);
                            }
                            return EMPTY;
                        })
                    );
                }

                // Obtener el sub del usuario (usa caché interno de AuthService)
                return from(authService.getCurrentPoolUser()).pipe(
                    switchMap((user) => {
                        if (user?.sub) {
                            // Crear promesa compartida para evitar llamadas concurrentes.
                            // IMPORTANTE: el .catch() vuelve a lanzar el error para que la
                            // promesa permanezca RECHAZADA cuando createSession falla —
                            // evitando que la petición original se reenvíe sin la cookie.
                            sessionCreationPromise = tenantService.createSession(user.sub, currentTenant)
                                .then(() => {
                                    sessionCreationPromise = null;
                                })
                                .catch((err) => {
                                    sessionCreationPromise = null;
                                    console.error('Error creando sesión de tenant:', err);
                                    throw err; // relanzar para que la promesa quede rechazada
                                });

                            // Crear sesión y luego continuar con la petición original.
                            // Si la sesión falló, redirigir a selección de tenant en lugar
                            // de reenviar la petición sin cookie (lo que causaría otro 403).
                            return from(sessionCreationPromise).pipe(
                                switchMap(() => next(reqWithCredentials)),
                                catchError((err) => {
                                    console.error('Sesión de tenant no pudo ser creada:', err);
                                    const status = err?.status;
                                    if (status === 401 || status === 403) {
                                        router.navigate(['/tenant-selection']);
                                    }
                                    return EMPTY;
                                })
                            );
                        }
                        // Si no hay user.sub, continuar con la petición
                        return next(reqWithCredentials);
                    })
                );
            }

            // Si tiene tenant_ids pero no currentTenant, redirigir a selección
            if (tenantIds.length > 0 && currentTenant === null) {
                console.warn('Usuario tiene tenants disponibles pero ninguno seleccionado. Redirigiendo a selección de tenant.');
                router.navigate(['/tenant-selection']);
                return EMPTY;
            }

            // Si no tiene tenants, redirigir a tenant-selection (ahí se verifican invitaciones pendientes)
            console.warn('Usuario no tiene tenants. Redirigiendo a selección de tenant.');
            router.navigate(['/tenant-selection']);
            return EMPTY;
        })
    );
};
